import { useLocalization, cn } from '@magicdoorinc/ui';
import { A } from '@solidjs/router';
import { Show, splitProps, createMemo, For } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { pickProps, TitleWithFeedbackIcon } from '~/components/common/FeedbackIcon/TitleWithFeedbackIcon';
import { formatTenantNames } from '~/components/tenants/utils';
import { UnitImage } from '~/components/units';
import { LeaseName } from './LeaseName';
import { LeasePeriod } from './LeasePeriod';
import { LeaseStatusElement } from './LeaseStatus';
import { isMonthToMonth } from './utils';
import type { ComponentProps, ValidComponent } from 'solid-js';
import type { TitleWithFeedbackIconProps } from '~/components/common/FeedbackIcon/TitleWithFeedbackIcon';
import type { HydratedPropertyDto } from '~/swagger/maintenance/Api';
import type { HydratedLeaseDto, LeaseDto, PropertyDto, UnitDto } from '~/swagger/portal/Api';
import type { CustomLink } from '~/types/display';
import type { Merge } from '~/utils/types';

export const LeaseTitle = <T extends ValidComponent>(
  props: Merge<
    ComponentProps<T>,
    {
      as?: T;
      class?: string;
      lease?: HydratedLeaseDto | LeaseDto;
      property?: PropertyDto | HydratedPropertyDto;
      unit?: UnitDto;
    } & Partial<TitleWithFeedbackIconProps> & {
        showTenants?: boolean;
        showAllTenants?: boolean;
        showStatus?: boolean;
        customLinks?: CustomLink[];
      }
  >
) => {
  const { t } = useLocalization();
  const [params, rest] = splitProps(props, ['as', 'class', 'lease']);

  const lease = createMemo(() => ({
    property: props.property,
    unit: props.unit,
    ...props.lease,
  }));

  return (
    <TitleWithFeedbackIcon
      class="h-fit"
      {...pickProps(rest)}
      type="leases"
      entityId={params.lease?.id}
      initalSelectedIcons={params.lease?.icons}>
      <Dynamic component={params.as || 'div'} class={cn('flex flex-col', params.class)} {...rest}>
        <div class="flex items-center gap-3">
          <UnitImage class="size-12 shrink-0 rounded-lg" unit={{ id: lease().unit?.id, imageId: lease().unit?.imageId }} />
          <div class="min-w-0 flex-1 space-y-1 break-words text-sm">
            <LeaseName lease={lease() as any} />

            <div class="flex flex-wrap items-start text-xs text-text-03">
              <div class="flex items-center pr-2">
                <Show when={isMonthToMonth(params.lease)}>
                  <span class="shrink-0 pr-1">{t('Month-to-month')}: </span>
                </Show>
                <LeasePeriod class="text-xs" lease={params.lease} />
              </div>
              <Show
                when={
                  (props.showTenants &&
                    params.lease?.tenants &&
                    params.lease.tenants.some((tenant: { isMovedOut: boolean }) => !tenant.isMovedOut)) ||
                  props.showAllTenants
                }>
                <span class="flex items-center border-l-2 border-separator pl-2 text-xs text-text-03 [.flex-wrap_&:first-child]:border-l-0 [.flex-wrap_&:first-child]:pl-0">
                  {t('Tenants')}:{' '}
                  {formatTenantNames(
                    params.lease?.tenants.filter((tenant: { isMovedOut: any }) => !tenant.isMovedOut || props.showAllTenants),
                    2
                  )}
                </span>
              </Show>
            </div>

            <Show when={props.customLinks && props.customLinks.length > 0}>
              <div class="flex flex-wrap items-start text-xs">
                <For each={props.customLinks}>
                  {(link, index) => (
                    <>
                      <A href={link.href} class="text-primary hover-allowed:hover:underline">
                        {link.displayName}
                      </A>
                      <Show when={index() < props.customLinks!.length - 1}>
                        <span class="mx-2 border-r border-separator">&nbsp;</span>
                      </Show>
                    </>
                  )}
                </For>
              </div>
            </Show>
            <Show when={props.showStatus && params.lease}>
              <LeaseStatusElement lease={params.lease!} isPill />
            </Show>
          </div>
        </div>
      </Dynamic>
    </TitleWithFeedbackIcon>
  );
};
